import { CartLinesUpdateEvent } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

const RECS_SECTION_ID = 'cart-drawer-recommendations';
const CART_SECTION_ID = 'cart-drawer-section';

/**
 * Cart drawer upsells: load recommendations on open, add products via Cart API.
 */
class PjCartDrawerUpsellController {
  /** @type {AbortController | null} */
  #fetchController = null;

  /** @type {string} */
  #loadedFor = '';

  constructor() {
    document.addEventListener(DrawerOpenEvent.eventName, this.#onDrawerEvent);
    document.addEventListener(CartLinesUpdateEvent.eventName, this.#onCartLinesUpdate);
    document.addEventListener('click', this.#onClick);

    // Restore / late-load path: drawer may already be open.
    const drawer = document.getElementById('cart-drawer');
    if (drawer?.hasAttribute('open')) {
      this.load();
    }
  }

  /** @returns {HTMLElement | null} */
  #root() {
    return document.querySelector('[data-pj-cart-drawer-upsell-root]');
  }

  /** @returns {HTMLElement | null} */
  #slot() {
    return document.querySelector('[data-pj-cart-upsell-slot]');
  }

  /**
   * @param {Event} event
   */
  #onDrawerEvent = (event) => {
    const drawer = event.target;
    if (!(drawer instanceof HTMLElement) || drawer.id !== 'cart-drawer') return;
    this.load();
  };

  /**
   * @param {CartLinesUpdateEvent} event
   */
  #onCartLinesUpdate = (event) => {
    const drawer = document.getElementById('cart-drawer');
    if (!drawer?.hasAttribute('open')) return;

    event.promise
      ?.then(() => {
        // Product id on the root may change after morph; clear cache and reload.
        this.#loadedFor = '';
        requestAnimationFrame(() => this.load({ force: true }));
      })
      .catch(() => {});
  };

  /**
   * @param {{ force?: boolean }} [options]
   */
  async load(options = {}) {
    const root = this.#root();
    const slot = this.#slot();
    if (!root || !slot) return;

    const productId = root.dataset.productId || '';
    if (!productId) {
      slot.replaceChildren();
      this.#loadedFor = '';
      return;
    }

    if (!options.force && this.#loadedFor === productId && slot.querySelector('.pj-cart-drawer-upsell__item')) {
      return;
    }

    this.#fetchController?.abort();
    this.#fetchController = new AbortController();

    const base =
      window.Theme?.routes?.product_recommendations_url ||
      `${window.Shopify?.routes?.root || '/'}recommendations/products`;
    const url = `${base}?section_id=${RECS_SECTION_ID}&product_id=${encodeURIComponent(productId)}&limit=4&intent=related`;

    try {
      const response = await fetch(url, { signal: this.#fetchController.signal });
      if (!response.ok) return;

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const upsell = doc.querySelector('[data-pj-cart-upsell]');
      if (!upsell?.querySelector('.pj-cart-drawer-upsell__item')) {
        slot.replaceChildren();
        this.#loadedFor = productId;
        return;
      }

      slot.replaceChildren(upsell);
      this.#loadedFor = productId;

      // Drawer markup is injected after parse — force slideshow scroll enablement.
      const slideshow = upsell.querySelector('slideshow-component');
      if (slideshow instanceof HTMLElement) {
        slideshow.setAttribute('in-viewport', '');
        slideshow.removeAttribute('disabled');
      }
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.warn('[pj-cart-drawer-upsell] Failed to load recommendations', error);
      }
    }
  }

  /**
   * @param {MouseEvent} event
   */
  #onClick = async (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('[data-pj-cart-upsell-add]');
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;
    if (!button.closest('#cart-drawer')) return;

    const variantId = Number(button.dataset.variantId);
    if (!variantId) return;

    event.preventDefault();
    button.disabled = true;

    const addUrl = window.Theme?.routes?.cart_add_url || `${window.Shopify?.routes?.root || '/'}cart/add.js`;
    const deferred = CartLinesUpdateEvent.createPromise();

    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(variantId), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(addUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections: CART_SECTION_ID,
          sections_url: window.location.pathname,
        }),
      });

      const payload = await response.json();
      if (!response.ok || payload.status) {
        throw new Error(payload.description || payload.message || 'Add to cart failed');
      }

      const cartUrl = window.Theme?.routes?.cart_url || '/cart';
      const ajaxCart = await fetch(`${cartUrl}.js`).then((res) => res.json());

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          items: ajaxCart.items,
          source: 'pj-cart-drawer-upsell',
          itemCount: 1,
          sections: payload.sections,
          didError: false,
        },
      });
    } catch (error) {
      deferred.reject(error);
      button.disabled = false;
      console.warn('[pj-cart-drawer-upsell] Add failed', error);
    }
  };
}

new PjCartDrawerUpsellController();
